// Libs
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Stack,
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseInput, BaseLabel, Loading } from '~/components/common';
// Others
import { EMPTY_STRING } from '~/utils/constants/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IFormSettingGoal } from '~/utils/interface/goal';
import { defaultValuesForm, schema } from './helper';
import { createSettingGoal, getDetailGoal } from '~/thunks/goal/goalThunk';
import { formatInputCurrency, removeEmptyObjects } from '~/utils/helper/common';
// Styles, images, icons
import styles from './SettingGoalModal.module.scss';
import { icons } from '~/assets';

type Props = {
  idGoal?: string;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const SettingGoalModal = (props: Props) => {
  //#region Destructuring Props
  const { idGoal, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/goal', 'common/base']);
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormSettingGoal>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetDetailGoal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailGoal = () => {
    setIsLoading(true);

    dispatch(getDetailGoal())
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { id, ...remainingData } = res?.data;
        const body: IFormSettingGoal = {
          ...remainingData,
          commissionCost: remainingData?.commissionCost || EMPTY_STRING,
          revenueCost: remainingData?.revenueCost || EMPTY_STRING,
        };

        reset(body);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleFormSettingGoal = (data: IFormSettingGoal) => {
    if (idGoal) {
      handleEditSettingGoal(data);
    } else {
      handleAddSettingGoal(data);
    }

    setIsLoading(true);
  };

  const handleAddSettingGoal = (data: IFormSettingGoal) => {
    if (!data) return;

    const convertData = removeEmptyObjects(data) as IFormSettingGoal;

    dispatch(createSettingGoal(convertData))
      .unwrap()
      .then(() => {
        handleCloseModal();
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditSettingGoal = (data: IFormSettingGoal) => {};

  const handleCloseModal = () => {
    onClose && onClose();
    reset();
  };
  //#endregion Handle Function

  return (
    <Dialog
      id='settingGoalModalComponent'
      aria-labelledby='settingGoalModal'
      open={true}
      onClose={handleCloseModal}
      fullWidth
      maxWidth={'md'}
    >
      <DialogTitle id='settingGoalModal' className={cx('header')}>
        <Stack direction={'column'} spacing={'10px'}>
          <Stack direction={'row'} spacing={10}>
            <BaseLabel label={t('setting_goal_form_title')} styleType='bigTitle' />
          </Stack>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <Grid2 columnSpacing={'24px'} rowSpacing={'24px'} container flex={1}>
          <Grid2 size={6}>
            <Controller
              control={control}
              name={'revenueCost'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    label={t('setting_goal_form_goal_revenue')}
                    value={value}
                    onChange={(event) => {
                      onChange(formatInputCurrency(event.target.value));
                    }}
                    errorMessage={errors?.revenueCost?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'commissionCost'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    label={t('setting_goal_form_goal_commission')}
                    value={value}
                    onChange={(event) => {
                      onChange(formatInputCurrency(event.target.value));
                    }}
                    errorMessage={errors?.commissionCost?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button variant='contained' className={cx('buttonSave')} onClick={handleSubmit(handleFormSettingGoal)}>
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default SettingGoalModal;
